import React, { useEffect, useState } from "react";
import {
  CustomLayout,
  PrimaryColor,
} from "../Styles/GlobalStyles.js/GlobalStyles";
import { Button, Grid, Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import TextField from "../form-components/TextField.js/TextField";
import Textarea from "../form-components/TextArea/TextArea";
import { useCountryCodes, useSubmitData } from "./service";
import moment from "moment";
import {
  allowMailCharacters,
  allowOnlyAlphabets,
  allowOnlyNumber,
} from "../Utils/HelperFunction/HelperFunction";

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email_id: yup
    .string()
    .required("Email id is required")
    .email("Enter a valid email address")
    .test(
      "is-valid-email-format",
      "Email must be in valid format (e.g. user@example.com)",
      (value) => {
        if (!value) return false;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(value);
      }
    ),
  contact_number: yup.string().required("Contact number is required"),
  message: yup.string().notRequired(),
});

const MeetingScheduler = () => {
  const [submitted, setSubmitted] = useState(false);
  const { data: countryCodes = [] } = useCountryCodes();
  const { mutate: addSubmitForm, data: dataStatus } = useSubmitData();

  // Handle success response
  useEffect(() => {
    if (dataStatus?.status === 200) {
      setSubmitted(true);
    }
  }, [dataStatus]);

  // Reset state on fresh load
  useEffect(() => {
    setSubmitted(false);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email_id: "",
      contact_number: "",
      country_code: { label: "91 (India)", value: "91" },
      message: "",
    },
  });

  const onSubmit = (data) => {
    const payload = {
      item: {
        name: data.name,
        email_id: data.email_id,
        country_code: data.country_code.value,
        contact_number: data.contact_number,
        message: data.message,
        contact_time: moment().format("DD-MM-YYYY HH:mm"),
      },
    };

    addSubmitForm(payload);
    reset();
  };

  return (
    <CustomLayout>
      <Box p={1}>
        {!submitted ? (
          <>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: "Outfit",
                fontSize: "25px",
              }}
            >
              Enter Details
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Grid container spacing={0.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="name"
                    placeholder="Enter Name"
                    label="Name"
                    isRequired={true}
                    {...register("name")}
                    errors={errors}
                    onInput={allowOnlyAlphabets}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="email_id"
                    placeholder="Enter Email"
                    label="Email"
                    isRequired={true}
                    {...register("email_id")}
                    onInput={allowMailCharacters}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="contact_number"
                    placeholder="Enter Contact Number"
                    label="Contact Number"
                    isRequired={true}
                    {...register("contact_number")}
                    errors={errors}
                    onInput={allowOnlyNumber}
                    maxLength={10}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Textarea
                    label="Message"
                    id="message"
                    name="Message"
                    {...register("message")}
                    placeholder="Please share anything that will help prepare for our meeting."
                    errors={errors}
                    isRequired={false}
                  />
                </Grid>
              </Grid>

              {/* Disclaimer */}
              <Typography
                variant="caption"
                color="textSecondary"
                mt={0}
                sx={{ display: "block", fontFamily: "Outfit" }}
              >
                By proceeding, you confirm that you have read and agree to{" "}
                <a
                  href=""
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: PrimaryColor,
                    textDecoration: "none",
                    fontFamily: "Outfit",
                    fontWeight: 550,
                  }}
                >
                  Atttic’s Terms of Use
                </a>{" "}
                and{" "}
                <a
                  href=""
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: PrimaryColor,
                    textDecoration: "none",
                    fontFamily: "Outfit",
                    fontWeight: 550,
                  }}
                >
                  Privacy Notice
                </a>
                .
              </Typography>

              {/* Submit Button */}
              <Box textAlign="center" mt={0.5}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: PrimaryColor,
                    fontWeight: 500,
                    borderRadius: "8px",
                    fontSize: "16px",
                    textTransform: "none",
                    fontFamily: "Outfit",
                  }}
                >
                  Submit
                </Button>
              </Box>
            </form>
          </>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            style={{ width: "100%" }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Outfit",
                fontWeight: 500,
                fontSize: "22px",
              }}
            >
              Thanks for connecting with us! <br />
              Our team will be in touch with you shortly.
            </Typography>
          </Box>
        )}
      </Box>
    </CustomLayout>
  );
};

export default MeetingScheduler;
